import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { organizationService, Organization } from '@/firebase/organizationService';
import { useNotification } from '@/contexts/NotificationContext';
import { Plus, Building2, X, Edit, Trash2, Users, CheckCircle, AlertTriangle, Key } from 'lucide-react';
import SSOConfigurationModal from './SSOConfigurationModal';
import { getUserSystemRoleForOrganization } from '@/utils/roleHelpers';

const OrganizationManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const { currentOrganization, loadOrganizations, setCurrentOrganization, createOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const [isNewSignup] = useState(searchParams.get('newSignup') === 'true');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showSSOModal, setShowSSOModal] = useState(false);
  const [selectedOrgForSSO, setSelectedOrgForSSO] = useState<Organization | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    industryType: '',
    description: ''
  });

  const industryTypes = [
    "Accommodation and Food Services",
    "Construction",
    "Real Estate and Property Management",
    "Healthcare and Social Assistance",
    "Manufacturing",
    "Retail and Wholesale Trade",
    "Transportation and Logistics",
    "Education",
    "Professional, Scientific, and Technical Services",
    "Energy and Utilities",
    "Environmental Services",
    "Finance and Insurance",
    "Bank",
    "Information Technology and Telecommunications",
    "Arts, Entertainment, and Recreation",
    "Public Administration and Government",
    "Nonprofit Organizations",
    "Agriculture, Forestry, Fishing, and Hunting",
    "Mining, Quarrying, and Oil & Gas Extraction",
    "Administrative and Support Services",
    "Waste Management and Remediation Services"
  ];

  useEffect(() => {
    loadAllUserOrganizations();
  }, [currentOrganization]);

  const loadAllUserOrganizations = async () => {
    setIsLoading(true);
    try {
      const orgMap = new Map<string, Organization>();
      
      // Get user data to access organizationIds array
      if (user?.id) {
        const { userService } = await import('@/firebase/database');
        const userResult = await userService.getUser(user.id);
        
        if (userResult.success && userResult.data) {
          const userData = userResult.data;
          const userOrgIds: string[] = [];
          
          // Get organizationIds array (support multiple organizations)
          if (userData.organizationIds && Array.isArray(userData.organizationIds)) {
            userOrgIds.push(...userData.organizationIds);
          }
          
          // Backward compatibility: if user has single organizationId, add it
          if (userData.organizationId && !userOrgIds.includes(userData.organizationId)) {
            userOrgIds.push(userData.organizationId);
          }
          
          // Load all organizations user belongs to
          if (userOrgIds.length > 0) {
            const orgsResult = await organizationService.getOrganizationsByIds(userOrgIds);
            if (orgsResult.success && orgsResult.data) {
              orgsResult.data.forEach(org => {
                if (org.id && !orgMap.has(org.id)) {
                  orgMap.set(org.id, org);
                }
              });
            }
          }
        }
      }
      
      // Also load organizations owned by user (in case they're not in organizationIds yet)
      if (user?.id) {
        const ownedResult = await organizationService.getOrganizationsByOwner(user.id);
        if (ownedResult.success && ownedResult.data) {
          // Merge without duplicates using Map
          ownedResult.data.forEach(org => {
            if (org.id && !orgMap.has(org.id)) {
              orgMap.set(org.id, org);
            }
          });
        }
      }
      
      // Convert Map to array to avoid duplicates
      const uniqueOrgs = Array.from(orgMap.values());
      console.log('📋 Loaded organizations:', uniqueOrgs.map(org => ({ id: org.id, name: org.name })));
      setOrganizations(uniqueOrgs);
    } catch (error: any) {
      console.error('Error loading organizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showNotification('Organization name is required', 'error');
      return;
    }

    if (!formData.industryType) {
      showNotification('Industry type is required', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // Trim organization name before creating
      const trimmedName = formData.name.trim();
      const result = await createOrganization(trimmedName, formData.industryType, formData.description || undefined);
      
      if (result.success) {
        showNotification('Organization created successfully!', 'success');
        setShowCreateModal(false);
        setFormData({ name: '', industryType: '', description: '' });
        await loadAllUserOrganizations();
        // Remove newSignup query param and redirect to home
        if (isNewSignup) {
          navigate('/organizations');
          setTimeout(() => navigate('/'), 500);
        }
      } else {
        showNotification(result.error || 'Failed to create organization', 'error');
      }
    } catch (error: any) {
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectOrganization = async (org: Organization) => {
    if (!org.id) return;
    
    try {
      await setCurrentOrganization(org);
      showNotification(`Switched to organization: ${org.name}`, 'success');
      // Reload page to apply organization context
      window.location.reload();
    } catch (error: any) {
      showNotification(`Error switching organization: ${error.message}`, 'error');
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Organizations
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage your organizations and switch between them
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary px-4 py-2 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Create Organization
            </button>
          </div>
        </div>

        {/* Current Organization */}
        {currentOrganization ? (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-800">{currentOrganization.name}</h3>
                  {currentOrganization.description && (
                    <p className="text-sm text-gray-600">{currentOrganization.description}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Owner ID: {currentOrganization.ownerId}
                  </p>
                </div>
              </div>
              {currentOrganization.ownerId === user?.id && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  You own this
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className={`mb-6 p-4 border rounded-lg ${isNewSignup ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-2">
              {isNewSignup ? (
                <CheckCircle className="w-5 h-5 text-blue-600" />
              ) : (
                <X className="w-5 h-5 text-yellow-600" />
              )}
              <div>
                <h3 className={`font-semibold ${isNewSignup ? 'text-blue-800' : 'text-yellow-800'}`}>
                  {isNewSignup ? 'Welcome! Create Your Organization' : 'No Active Organization'}
                </h3>
                <p className={`text-sm ${isNewSignup ? 'text-blue-700' : 'text-yellow-700'}`}>
                  {isNewSignup 
                    ? 'Create your organization to start managing compliance tasks, locations, and templates.'
                    : 'Create or join an organization to get started'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Organizations List */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading organizations...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {organizations.map((org) => (
              <div
                key={org.id}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  currentOrganization?.id === org.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
                onClick={() => handleSelectOrganization(org)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-800">{org.name}</h3>
                  </div>
                  {currentOrganization?.id === org.id && (
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  )}
                </div>
                {org.description && (
                  <p className="text-sm text-gray-600 mb-3">{org.description}</p>
                )}
                <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
                  <span>ID: {org.id?.substring(0, 8)}...</span>
                  {org.ownerId === user?.id && (
                    <span className="px-2 py-1 bg-gray-100 rounded">Owner</span>
                  )}
                </div>
                {(() => {
                  // Check if user is admin for this organization
                  const userSystemRole = getUserSystemRoleForOrganization(user, org.id || '');
                  const isAdmin = userSystemRole === 'admin';
                  
                  return isAdmin && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrgForSSO(org);
                          setShowSSOModal(true);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <Key className="w-4 h-4" />
                        {org.sso?.enabled ? 'Configure SSO' : 'Set up SSO'}
                      </button>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {organizations.length === 0 && !isLoading && (
          <div className="text-center py-12 border border-gray-200 rounded-lg">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">No Organizations Yet</h3>
            <p className="text-sm text-gray-600 mb-4">
              Create your first organization to get started
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary px-4 py-2 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Create Organization
            </button>
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">Create Organization</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ name: '', industryType: '', description: '' });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateOrganization} className="p-6 space-y-4">
              {/* Warning Banner */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-900 mb-1">Important Notice</p>
                    <p className="text-xs text-orange-800">
                      Only create a new organization if you're sure your admin hasn't already created one. If you're part of an existing organization, contact your admin to add you as a user.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="Acme Corporation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry Type *
                </label>
                <select
                  required
                  value={formData.industryType}
                  onChange={(e) => setFormData({ ...formData, industryType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select an industry type</option>
                  {industryTypes.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="Optional description..."
                  rows={3}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <p className="font-medium mb-1">ℹ️ What happens next:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>You'll become the admin of this organization</li>
                  <li>You can invite team members</li>
                  <li>All your data will be isolated to this organization</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', industryType: '', description: '' });
                  }}
                  className="btn-secondary px-5 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary px-5 py-2"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Floating Getting Started button on empty state */}
      {organizations.length === 0 && !isLoading && (
        <a
          href="/help/getting-started"
          className="fixed bottom-24 right-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-4 py-2 z-50"
          title="Getting Started"
        >
          Getting Started
        </a>
      )}

      {/* SSO Configuration Modal */}
      <SSOConfigurationModal
        isOpen={showSSOModal}
        onClose={() => {
          setShowSSOModal(false);
          setSelectedOrgForSSO(null);
        }}
        organization={selectedOrgForSSO}
      />
    </div>
  );
};

export default OrganizationManagement;
